import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';

export const CartPage = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, clearCart } = useCart();

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen pt-20 pb-12 flex flex-col items-center justify-center" data-testid="cart-page-empty">
        <ShoppingCart className="w-20 h-20 text-gray-600 mb-6" />
        <h1 className="text-2xl font-bold text-white mb-2 font-outfit">Votre panier est vide</h1>
        <p className="text-gray-400 mb-6">Explorez notre boutique pour trouver vos cartes</p>
        <Link to="/shop">
          <Button className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white">
            Découvrir les produits
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="cart-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/shop" className="text-gray-400 hover:text-white" data-testid="back-to-shop">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-white font-outfit">Mon panier</h1>
              <p className="text-gray-400">{cart.items.length} article{cart.items.length > 1 ? 's' : ''}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={clearCart}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid="clear-cart-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Vider le panier
          </Button>
        </div>

        {/* Cart Items */}
        <div className="space-y-4 mb-8">
          {cart.items.map((item) => (
            <div 
              key={item.product_id} 
              className="bg-[#121212] border border-white/10 rounded-xl p-4 flex gap-4"
              data-testid={`cart-item-${item.product_id}`}
            >
              <Link to={`/product/${item.product_id}`}>
                <img 
                  src={item.image_url} 
                  alt={item.name} 
                  className="w-24 h-24 object-cover rounded-lg hover:opacity-80 transition-opacity"
                />
              </Link>
              
              <div className="flex-1 min-w-0">
                <Link to={`/product/${item.product_id}`}>
                  <h3 className="text-white font-semibold hover:text-fuchsia-400 transition-colors">
                    {item.name}
                  </h3>
                </Link>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    item.game === 'yugioh' ? 'badge-yugioh' : 
                    item.game === 'magic' ? 'badge-magic' : 'badge-pokemon'
                  }`}>
                    {item.game === 'yugioh' ? 'Yu-Gi-Oh!' : item.game === 'magic' ? 'Magic' : 'Pokémon'}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    item.category === 'deck' ? 'badge-deck' : 
                    item.category === 'booster' ? 'badge-booster' : 'badge-single'
                  }`}>
                    {item.category === 'deck' ? 'Deck' : item.category === 'booster' ? 'Booster' : 'Carte'}
                  </span>
                </div>
                <p className="text-fuchsia-400 font-bold mt-2">{item.price.toFixed(2)} €</p>
              </div>

              <div className="flex flex-col items-end justify-between">
                <button
                  onClick={() => removeFromCart(item.product_id)}
                  className="text-gray-500 hover:text-red-400 transition-colors"
                  data-testid={`remove-item-${item.product_id}`}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-2 bg-white/5 rounded-lg p-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                    className="h-8 w-8 text-gray-400 hover:text-white"
                    data-testid={`decrease-${item.product_id}`}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-8 text-center text-white font-semibold">{item.quantity}</span>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={async () => {
                      try {
                        await updateQuantity(item.product_id, item.quantity + 1);
                      } catch (err) {
                        toast.error(err.message);
                      }
                    }}
                    disabled={item.quantity >= (item.stock || 999)}
                    className="h-8 w-8 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`increase-${item.product_id}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {item.stock !== undefined && (
                  <span className={`text-xs mt-1 ${item.quantity >= item.stock ? 'text-orange-400' : 'text-gray-500'}`}>
                    {item.quantity >= item.stock ? 'Stock max atteint' : `${item.stock} dispo`}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="bg-[#121212] border border-white/10 rounded-xl p-6">
          <div className="space-y-3 mb-6">
            <div className="flex justify-between text-gray-400">
              <span>Sous-total</span>
              <span>{cart.total.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>Livraison</span>
              <span className="text-gray-500 text-sm">Calculée à l'étape suivante</span>
            </div>
            <div className="border-t border-white/10 pt-3 flex justify-between text-xl font-bold text-white">
              <span>Total</span>
              <span>{cart.total.toFixed(2)} €</span>
            </div>
          </div>
          
          <Button
            onClick={() => navigate('/checkout')}
            className="w-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white py-6 text-lg glow-primary"
            data-testid="proceed-to-checkout"
          >
            Passer la commande
          </Button>
        </div>
      </div>
    </div>
  );
};
